import "../../../components/common/Tables/DataTable.css"
import React, {useMemo, useState} from "react";
import {useSelector} from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import RootState from "../../../redux/states/root.state";
import { useReportTimeFormat } from "../../SpaReport/reportTime";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { palName } from "../../../utils/brand";

// Firmware upgrade requests: the Upgrade Requests page (all spas) and the Spa Report's upgrade
// history (one spa, no Spa column). Rendered inside a DataTable card whose toolbar the page
// owns, so this is the scrolling table and its pager.

type Order = 'asc' | 'desc';
const headers = [
  {id:'status', label:'Status'},
  {id:'type', label:'Type'},
  {id:'version', label:'Version'},
  {id:'source', label:'Source'},
  {id:'spa_id', label:'Spa'},
  {id:'created_at', label:'Sent'},
  {id:'updated_at', label:'Updated'},
];

// The request's lifecycle, in a tone (the API's wording is kept as the label)
const statusTone = (status: string): string => {
  switch ((status ?? '').toLowerCase()) {
    case 'fulfilled': case 'fufilled': case 'complete': case 'completed': case 'success': return 'good';
    case 'error': case 'err': case 'failed': return 'bad';
    case 'sent': return 'info';
    case 'pending': return 'caution';
    default: return 'neutral';
  }
};

const TYPE_LABEL: Record<string, string> = {
  yocto: 'Yocto',
  lpc: 'LPC',
  spaboy: palName,
};

// Sorted on this page only (the API doesn't sort requests)
const sortValue = (request: any, column: string) => {
  if (column === 'created_at' || column === 'updated_at') {
    const t = request?.[column] ? new Date(request[column]).getTime() : NaN;
    return Number.isNaN(t) ? '' : String(t).padStart(15, '0');
  }
  return (request?.[column] ?? '').toString().toLowerCase();
};

interface HistoryTableProps {
  page?: 'reports' | 'requests';
}

const HistoryTable: React.FC<HistoryTableProps> = ({page='requests'}) => {
  const navigate = useNavigate();
  // Spa time when rendered inside a spa report; the viewer's local time elsewhere.
  const formatTime = useReportTimeFormat();
  const {data: history, loading:isLoading, meta} = useSelector((state: RootState) => state.upgradeRequests);
  const [searchParams, setSearchParams] = useSearchParams();

  // Table Sort
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState('created_at');

  const visibleHeaders = headers.filter((header) => page === 'requests' || header.id !== 'spa_id');

  const rows = useMemo(() => {
    if (!orderBy) return history;
    const direction = order === 'asc' ? 1 : -1;
    return [...(history ?? [])].sort((a: any, b: any) =>
      sortValue(a, orderBy).localeCompare(sortValue(b, orderBy), undefined, {numeric: true}) * direction);
  }, [history, orderBy, order]);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header_id);
  };

  const currentPage = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? meta?.last_page ?? 1;
  const total = meta?.total ?? 0;

  // Merge rather than replace, so the filters survive turning the page
  const goToPage = (value: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(value));
    setSearchParams(next);
  };

  const renderCell = (id: string, request: any) => {
    switch (id) {
      case 'status':
        return request?.status
          ? <span className={`dt-pill ${statusTone(request.status)}`}>{request.status}</span>
          : <span className="dt-na">—</span>;

      case 'type':
        return request?.type
          ? <span className="dt-tag">{TYPE_LABEL[request.type] ?? request.type}</span>
          : <span className="dt-na">—</span>;

      case 'version':
        return request?.version ? <span className="dt-strong dt-num">{request.version}</span> : <span className="dt-na">—</span>;

      case 'source':
        return request?.source ?? <span className="dt-na">—</span>;

      case 'spa_id':
        return request?.spa_id ? (
          <a
            href={`/spa/${request.spa_id}`}
            className="dt-guid"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View spa"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/spa/${request.spa_id}`);
            }}
          >
            {request.spa_id}
          </a>
        ) : <span className="dt-na">—</span>;

      case 'created_at':
        return request?.created_at ? <span className="dt-date">{formatTime(request.created_at)}</span> : <span className="dt-na">—</span>;

      case 'updated_at':
        return request?.updated_at ? <span className="dt-date">{formatTime(request.updated_at)}</span> : <span className="dt-na">—</span>;

      default:
        return null;
    }
  };

  return (
    <>
      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading upgrade requests"/></div>
        ) : rows?.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {visibleHeaders.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((request: any, i: number)=>(
                <tr key={request?.id ?? i}>
                  {visibleHeaders.map((header)=>(
                    <td key={header.id}>{renderCell(header.id, request)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Upgrade Requests Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={history?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Request' : 'Requests'}
        page={currentPage}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </>
  );
};

export default HistoryTable;
